import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrap from '../components/PageWrap';
import useDocumentMeta from '../hooks/useDocumentMeta';
import routeMetaFor from '../utils/routeMeta';
import { SUPPORT_EMAIL, SUPPORT_PAGE } from '../data/siteContent';
import { APP_LEGAL } from '../data/legalContent';
import { emailJsConfigError, missingEmailJsVars, requireEmailJsConfig } from '../config/emailjs';

// Support routes users ask for most often after emailing: each app's own legal
// pages. Links only — the form, its validation, and its EmailJS wiring are
// unchanged by task T05.
const APP_LEGAL_LINKS = [
  {
    app: APP_LEGAL.nutriscan.name,
    links: [
      { to: '/apps/nutriscan/privacy', label: 'Privacy Policy' },
      { to: '/apps/nutriscan/terms', label: 'Terms of Service' },
      { to: '/apps/nutriscan/account-deletion', label: 'Account Deletion' },
    ],
  },
  {
    app: APP_LEGAL.cardcycle.name,
    links: [
      { to: '/apps/cardcycle/privacy', label: 'Privacy Notice' },
      { to: '/apps/cardcycle/terms', label: 'Terms of Use' },
      { to: '/apps/cardcycle/account-deletion', label: 'Data Deletion' },
    ],
  },
];

/**
 * Client-side abuse controls (task T06). This architecture has no backend and
 * therefore no server-side rate limiting, so these three raise the cost of a
 * scripted submission; EmailJS account-level limits are the other half.
 */
// A field no sighted, keyboard, or screen-reader user can reach. Anything in it
// came from something filling every input it found.
export const HONEYPOT_FIELD = 'company';
// A visitor needs longer than this to read the form and type four fields; a
// script posts almost immediately after load.
export const MIN_SUBMIT_DELAY_MS = 3000;
// How long the form refuses a further send after a successful one.
export const SUBMIT_COOLDOWN_MS = 60000;

// Resolved once: Create React App inlines these at build time, so the result
// cannot change while the page is open.
const MISSING_CONFIG = missingEmailJsVars();
const EMAILJS_READY = MISSING_CONFIG.length === 0;

export default function Contact() {
  useDocumentMeta(routeMetaFor('/contact'));
  const formRef = useRef();
  const mountedAtRef = useRef(Date.now());
  const [form, setForm]     = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [cooldownUntil, setCooldownUntil] = useState(0);

  // A build without EmailJS configuration has to say so rather than appear to
  // send. This replaces the committed credential fallbacks removed in T06.
  useEffect(() => {
    if (!EMAILJS_READY) console.error(emailJsConfigError(MISSING_CONFIG));
  }, []);

  // Re-render when the cooldown expires so the button re-enables on its own.
  useEffect(() => {
    if (!cooldownUntil) return undefined;
    const remaining = cooldownUntil - Date.now();
    if (remaining <= 0) { setCooldownUntil(0); return undefined; }
    const timer = setTimeout(() => setCooldownUntil(0), remaining);
    return () => clearTimeout(timer);
  }, [cooldownUntil]);

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = 'Name is required.';
    if (!form.email.trim())   e.email   = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email.';
    if (!form.subject.trim()) e.subject = 'Subject is required.';
    if (!form.message.trim()) e.message = 'Message is required.';
    return e;
  };

  const change = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(ev => ({ ...ev, [name]: '' }));
  };

  const submit = async (e) => {
    e.preventDefault();

    // Honeypot: drop it without a word, so an automated filler learns nothing.
    const trap = formRef.current && formRef.current.elements[HONEYPOT_FIELD];
    if (trap && trap.value.trim() !== '') { setStatus('success'); return; }

    if (Date.now() < cooldownUntil) { setStatus('cooldown'); return; }

    // Time-trap. A real visitor is asked to send again rather than shown a
    // success they did not get.
    if (Date.now() - mountedAtRef.current < MIN_SUBMIT_DELAY_MS) { setStatus('too-fast'); return; }

    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setStatus('sending');
    try {
      const { serviceId, templateId, publicKey } = requireEmailJsConfig();
      await emailjs.sendForm(serviceId, templateId, formRef.current, publicKey);
      setStatus('success');
      setCooldownUntil(Date.now() + SUBMIT_COOLDOWN_MS);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch { setStatus('error'); }
  };

  const cooling = cooldownUntil > 0;
  // Every input is associated with its label by id, and an error message is
  // announced through aria-describedby rather than by colour alone.
  const fieldProps = (name) => ({
    id: `contact-${name}`,
    name,
    value: form[name],
    onChange: change,
    className: errors[name] ? 'err' : '',
    'aria-invalid': errors[name] ? 'true' : undefined,
    'aria-describedby': errors[name] ? `contact-${name}-error` : undefined,
    'data-testid': `input-${name}`,
  });

  return (
    <PageWrap testId="contact-page">
      <Navbar />
      <section className="page-hero">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="eyebrow reveal">{SUPPORT_PAGE.eyebrow}</div>
          <h1 className="reveal d1">{SUPPORT_PAGE.title}</h1>
          <p className="lead reveal d2">{SUPPORT_PAGE.lead}</p>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <div className="split-2 contact">

            {/* Sidebar */}
            <div className="reveal">
              <div className="card" style={{ marginBottom: '16px' }}>
                <h3 style={{ marginBottom: '20px' }}>{SUPPORT_PAGE.directHeading}</h3>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div className="icon-box" style={{ flexShrink: 0, marginBottom: 0 }}><i className="fas fa-envelope"></i></div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Email</div>
                    <a href={`mailto:${SUPPORT_EMAIL}`} style={{ fontSize: '0.9rem', color: 'var(--text)' }} data-testid="support-email">{SUPPORT_EMAIL}</a>
                  </div>
                </div>
              </div>

              <div className="card" data-testid="support-legal-links">
                <h3 style={{ marginBottom: '16px' }}>App legal pages</h3>
                {APP_LEGAL_LINKS.map((entry) => (
                  <div key={entry.app} style={{ marginBottom: '12px' }} data-testid={`support-legal-${entry.app.toLowerCase()}`}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '4px' }}>{entry.app}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', fontSize: '0.82rem' }}>
                      {entry.links.map((link) => (
                        <Link key={link.to} to={link.to}>{link.label}</Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="card reveal d2">
              {status === 'success' ? (
                <div className="contact-success" data-testid="contact-success">
                  <div className="check"><i className="fas fa-circle-check"></i></div>
                  <h2>Message sent!</h2>
                  <p style={{ marginTop: '10px' }}>Thanks for reaching out.</p>
                  <button className="btn btn-primary" style={{ marginTop: '24px' }} onClick={() => setStatus('idle')}>Send another</button>
                </div>
              ) : (
                <form ref={formRef} onSubmit={submit} noValidate data-testid="contact-form">
                  <h3 style={{ marginBottom: '24px' }}>{SUPPORT_PAGE.formHeading}</h3>
                  <div className="field-row">
                    <div className="field">
                      <label htmlFor="contact-name">Name <span className="req">*</span></label>
                      <input type="text" placeholder="Jane Smith" {...fieldProps('name')} />
                      {errors.name && <div className="err-msg" id="contact-name-error">{errors.name}</div>}
                    </div>
                    <div className="field">
                      <label htmlFor="contact-email">Email <span className="req">*</span></label>
                      <input type="email" placeholder="jane@example.com" {...fieldProps('email')} />
                      {errors.email && <div className="err-msg" id="contact-email-error">{errors.email}</div>}
                    </div>
                  </div>
                  <div className="field">
                    <label htmlFor="contact-subject">Subject <span className="req">*</span></label>
                    <input type="text" placeholder="e.g. NutriScan feedback" {...fieldProps('subject')} />
                    {errors.subject && <div className="err-msg" id="contact-subject-error">{errors.subject}</div>}
                  </div>
                  <div className="field">
                    <label htmlFor="contact-message">Message <span className="req">*</span></label>
                    <textarea rows={6} placeholder="Tell us what's on your mind…" {...fieldProps('message')} />
                    {errors.message && <div className="err-msg" id="contact-message-error">{errors.message}</div>}
                  </div>
                  <input type="hidden" name="to_email" value="support@tickleapps.com" />
                  {/*
                    Honeypot. aria-hidden keeps it out of the accessibility tree
                    and tabIndex -1 out of the tab order, so no keyboard or
                    screen-reader visitor can reach it, let alone fill it.
                  */}
                  <div aria-hidden="true" style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                    <label htmlFor="contact-company">Company</label>
                    <input
                      id="contact-company"
                      name={HONEYPOT_FIELD}
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      defaultValue=""
                      data-testid="input-company"
                    />
                  </div>
                  {status === 'error' && (
                    <div className="callout alert" style={{ marginBottom: '16px' }}>
                      <i className="fas fa-triangle-exclamation"></i>
                      <span>Couldn't send. Please email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> directly.</span>
                    </div>
                  )}
                  {status === 'too-fast' && (
                    <div className="callout alert" style={{ marginBottom: '16px' }} data-testid="contact-too-fast">
                      <i className="fas fa-triangle-exclamation"></i>
                      <span>That arrived quicker than expected, so it was not sent. Please press send again.</span>
                    </div>
                  )}
                  {(status === 'cooldown' || cooling) && (
                    <div className="callout alert" style={{ marginBottom: '16px' }} data-testid="contact-cooldown">
                      <i className="fas fa-clock"></i>
                      <span>Your message is on its way. Please pause before sending another, or email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</span>
                    </div>
                  )}
                  {!EMAILJS_READY && (
                    <div className="callout alert" style={{ marginBottom: '16px' }} data-testid="contact-unconfigured">
                      <i className="fas fa-triangle-exclamation"></i>
                      <span>The contact form is not available in this build. Please email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> directly.</span>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={status === 'sending' || cooling || !EMAILJS_READY}
                    className="btn btn-primary btn-lg"
                    data-testid="contact-submit"
                  >
                    {status === 'sending' ? <><i className="fas fa-spinner fa-spin"></i> Sending…</> : <><i className="fas fa-paper-plane"></i> Send message</>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </PageWrap>
  );
}
